/**
 * The Almanac (D244): the year's upgrades as a tree (D454), and the year's goals with how far
 * each has come. The tree is four branches of tiers; any open step can be bought in any order,
 * and a fork closes its other side until the world is passed on. Goals and the collection sit
 * under the second segment.
 */
import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { idleCollection, idleGoals } from '@hedgerow/sim';
import { flyCoins, playSound, popNumber } from '../juice/index.ts';
import { Button } from '../ui/Button.tsx';
import { Icon } from '../ui/Icon.tsx';
import { useScientific } from './notation.ts';
import { idleContent, useIdle } from './store.ts';
import { cap, lower, t as term } from './terms.ts';
import { Figs } from './BuyButton.tsx';
import {
  BookSwitch,
  CARD10,
  IconTile,
  NOTE,
  SectionHead,
  TAB_BODY,
  useArrival,
  useArrive,
  useBadges,
} from './tab-ui.tsx';
import { firstSegment, newestGoals } from './badges.ts';
import { PINNED_HEAD, TreePanel } from './TreeView.tsx';
import { almanacModel } from './tree-view.ts';
import { markTreeNoteSeen, treeNoteSeen } from './tree-note.ts';
import {
  collectionGroups,
  goalMetText,
  goalRows,
  refusalText,
  type CollectionRow,
  type GoalRow,
} from './view.ts';

const SMALL: JSX.CSSProperties = NOTE;

/** Buy, with the sound, the pop and the coins it cost. True when it went through. */
function buyUpgrade(id: string, el: Element): boolean {
  const r = useIdle.getState().act({ type: 'buyUpgrade', upgrade: id });
  if (!r.ok) {
    playSound('error');
    popNumber(el, refusalText(idleContent, r));
    return false;
  }
  // A page of the almanac turns: a sound, the pop and the coins it cost.
  playSound('buy');
  popNumber(el, 'Bought');
  flyCoins(el, { count: 4 });
  return true;
}

/** D457: a branch's next mastery level, with the same sound and pop as a step. */
function buyMastery(branch: string, el: Element): void {
  const r = useIdle.getState().act({ type: 'buyMastery', branch });
  if (!r.ok) {
    playSound('error');
    popNumber(el, refusalText(idleContent, r));
    return;
  }
  playSound('buy');
  popNumber(el, 'Bought');
  flyCoins(el, { count: 4 });
}

/** D454: for a world that was open before the tree, said once, with a 44px Close. */
function TreeNote({ onClose }: { onClose: () => void }) {
  return (
    <div
      data-testid="tree-note"
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        alignItems: 'center',
        gap: 10,
        padding: 10,
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--line)',
        background: 'color-mix(in srgb, var(--accent) 8%, var(--surface))',
      }}
    >
      <span style={{ font: '400 var(--fs-label)/1.4 var(--font-ui)' }}>
        Your {cap(term('almanac'))} is a tree now. Everything you bought this year is still bought.
        Buy any open step, in any order; each tier opens the next.
      </span>
      <Button data-testid="tree-note-close" onClick={onClose}>
        Close
      </Button>
    </div>
  );
}

/** D334: one goal at its tier on show, with a pip for each tier. D431: a met goal is green. */
function GoalCard({ row: g }: { row: GoalRow }) {
  const pct = Math.floor(g.progress * 100);
  return (
    <div
      data-testid={`goal-${g.id}`}
      style={{
        ...CARD10,
        gap: 6,
        padding: '8px 10px',
        borderRadius: 'var(--radius-lg)',
        // D442: a met goal takes an accent border and its pay in green.
        ...(g.met ? { borderColor: 'var(--accent)' } : {}),
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <IconTile name={g.met ? 'check' : 'sparkles'} size={40} icon={20} />
        <span style={{ font: '600 var(--fs-name)/1.3 var(--font-ui)', flex: 1 }}>{g.name}</span>
        <span
          class={g.met ? undefined : 'num'}
          style={{
            font: g.met
              ? '600 var(--fs-caption)/1 var(--font-ui)'
              : '600 var(--fs-caption)/1 var(--font-num)',
            color: g.met ? 'var(--ink)' : 'var(--ink-2)',
            whiteSpace: 'nowrap',
          }}
        >
          {g.met ? 'Met' : `${pct}%`}
        </span>
      </div>
      <div
        class="juice-bar"
        role="progressbar"
        aria-label={g.name}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        style={{ height: '8px', borderRadius: '8px' }}
      >
        <span
          style={{
            background: g.met ? 'var(--coin)' : 'var(--accent)',
            transform: `scaleX(${g.progress})`,
          }}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ ...SMALL, flex: 1, color: g.met ? 'var(--green)' : 'var(--ink-2)' }}>
          <Figs text={g.reward} />
        </span>
        {g.tiers > 1 && (
          <span
            style={{
              ...SMALL,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              color: g.met ? 'var(--ink)' : 'var(--ink-2)',
            }}
            data-testid={`goal-${g.id}-tiers`}
            aria-label={`${g.metTiers} of ${g.tiers} stages met`}
          >
            <Figs text={`Stage ${g.met ? g.tiers : g.tier} of ${g.tiers}`} />
            {Array.from({ length: g.tiers }, (_, i) => (
              <span
                key={i}
                aria-hidden="true"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  // D372: the dots fill to the stage shown.
                  background: i < (g.met ? g.tiers : g.tier) ? 'var(--coin)' : 'transparent',
                  border: '1px solid var(--line)',
                }}
              />
            ))}
          </span>
        )}
      </div>
    </div>
  );
}

/** D290: one square, found or an outline, in the collection's grid. */
function CollectionCell({ row }: { row: CollectionRow }) {
  return (
    <div
      data-testid={`collection-${row.kind}-${row.id}`}
      title={row.found ? row.name! : undefined}
      style={{
        display: 'grid',
        justifyItems: 'center',
        gap: 4,
        padding: '8px 4px',
        borderRadius: 'var(--radius-sm)',
        border: `1px ${row.found ? 'solid var(--line)' : 'dashed var(--line)'}`,
        background: row.found ? 'var(--surface)' : 'transparent',
        opacity: row.found ? 1 : 0.4,
      }}
    >
      <Icon name={row.icon} size={22} />
      <span
        style={{
          font: '500 var(--fs-note)/1.2 var(--font-ui)',
          textAlign: 'center',
          color: 'var(--ink-2)',
        }}
      >
        {row.found ? row.name : '?'}
      </span>
    </div>
  );
}

/**
 * D461: a goal tier met since Goals was last looked at: ringed, `New` after its name, what it
 * paid under it and a tick; no buttons, since goals pay as soon as they are met.
 */
function NewGoalCard({ name, paid, testId }: { name: string; paid: string; testId: string }) {
  return (
    <div
      class="idle-waiting"
      data-testid={testId}
      style={{
        ...CARD10,
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        alignItems: 'center',
        gap: 10,
        padding: '8px 10px',
        borderRadius: 'var(--radius-lg)',
      }}
    >
      <span class="idle-waiting-dot idle-waiting-edge" aria-hidden="true" />
      <span style={{ display: 'grid', gap: 2, minWidth: 0 }}>
        <span style={{ font: '600 var(--fs-name)/1.3 var(--font-ui)' }}>
          {name}
          <span
            style={{
              display: 'inline-block',
              marginLeft: 6,
              padding: '2px 6px',
              borderRadius: 4,
              background: 'var(--accent)',
              color: 'var(--accent-ink)',
              font: '700 var(--fs-badge)/1.2 var(--font-ui)',
              verticalAlign: 2,
            }}
          >
            New
          </span>
        </span>
        <span style={{ ...SMALL, color: 'var(--green)' }}>
          <Figs text={`Paid: ${paid.charAt(0).toLowerCase()}${paid.slice(1)}`} />
        </span>
      </span>
      <span style={{ color: 'var(--accent)', display: 'grid' }} aria-label="Met">
        <Icon name="check" size={20} />
      </span>
    </div>
  );
}

/**
 * D462: the row under the newest five: "and N more" with a chevron, full width and dashed, opening
 * the rest in place; opened, it sits at the end of the list as "Show fewer" and closes it.
 */
function MoreRow({ open, more, onToggle }: { open: boolean; more: number; onToggle: () => void }) {
  return (
    <button
      type="button"
      data-testid="goals-new-more"
      aria-expanded={open}
      onClick={onToggle}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        width: '100%',
        minHeight: 44,
        padding: '0 12px',
        border: '1px dashed var(--line)',
        borderRadius: 12,
        background: 'transparent',
        color: 'var(--accent)',
        font: '600 var(--fs-label)/1.2 var(--font-ui)',
        cursor: 'pointer',
      }}
    >
      {open ? 'Show fewer' : <Figs text={`and ${more} more`} />}
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.4"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d={open ? 'm6 15 6-6 6 6' : 'm6 9 6 6 6-6'} />
      </svg>
    </button>
  );
}

export function AlmanacTab() {
  const world = useIdle((s) => s.world);
  const worldId = useIdle((s) => s.worldId);
  useScientific();
  // D461: a badged tab opens on Goals when goals were met since it was last looked at.
  const badge = useBadges()?.almanac;
  const arrived = useArrival();
  const [page, setPage] = useState<'tree' | 'goals'>(() =>
    arrived && badge && firstSegment(badge) === 'goals' ? 'goals' : 'tree',
  );
  const root = useRef<HTMLDivElement>(null);
  useArrive(root, arrived && (badge?.count ?? 0) > 0);
  // D461: the marks and the count go once Goals is left.
  useEffect(() => {
    if (page !== 'goals') return;
    return () => useIdle.getState().seeNew('goals');
  }, [page]);
  const [noteSeen, setNoteSeen] = useState(() => treeNoteSeen(worldId));
  // D461: the newest five, then "and N more" opens the rest in place.
  const [allNew, setAllNew] = useState(false);
  if (!world) return null;
  const fresh = (badge?.items ?? [])
    .map((k) => ({ key: k, text: goalMetText(idleContent, world, k) }))
    .filter((g) => g.text !== null);
  const cut = newestGoals(fresh);
  const freshShown = allNew ? newestGoals(fresh, Infinity).shown : cut.shown;
  const tree = almanacModel(world, idleContent);
  const goals = goalRows(idleContent, idleGoals(world, idleContent));
  // D334: each tier counts as a goal.
  const met = goals.reduce((a, g) => a + g.metTiers, 0);
  const total = goals.reduce((a, g) => a + g.tiers, 0);
  const collection = collectionGroups(idleContent, idleCollection(world, idleContent));
  const book = (
    <BookSwitch
      value={page}
      onChange={setPage}
      options={[
        {
          value: 'tree',
          label: 'Tree',
          testId: 'almanac-switch-tree',
        },
        {
          value: 'goals',
          label: 'Goals',
          badge: badge?.segments.goals,
          testId: 'almanac-switch-goals',
        },
      ]}
    />
  );
  const showNote = world.permanent.treeNote === true && !noteSeen;
  return (
    <div
      ref={root}
      data-screen-label={term('tabAlmanac')}
      aria-label={term('almanac')}
      style={TAB_BODY}
    >
      {/* D433, D442, D454: no screen title; the control is Tree and Goals. */}
      {/* The same pinned group as the Tree's, so the switcher stays put between pages. */}
      {page === 'goals' && (
        <div data-testid="almanac-goals-sticky" style={PINNED_HEAD}>
          {book}
        </div>
      )}

      {page === 'tree' && (
        <section data-testid="almanac-upgrades">
          <TreePanel
            model={tree}
            testId="almanac-tree"
            buy={buyUpgrade}
            mastery={buyMastery}
            head={book}
            above={
              showNote ? (
                <TreeNote
                  onClose={() => {
                    markTreeNoteSeen(worldId);
                    setNoteSeen(true);
                  }}
                />
              ) : undefined
            }
          />
        </section>
      )}
      {page === 'goals' && (
        <>
          {fresh.length > 0 && (
            <section style={{ display: 'grid', gap: 8 }} data-testid="goals-new">
              <SectionHead title="Just met" count={fresh.length} />
              {freshShown.map((g) => (
                <NewGoalCard
                  key={g.key}
                  testId={`goal-new-${g.key}`}
                  name={g.text!.name}
                  paid={g.text!.reward}
                />
              ))}
              {cut.more > 0 && (
                <MoreRow open={allNew} more={cut.more} onToggle={() => setAllNew(!allNew)} />
              )}
              <span style={{ ...SMALL, margin: '0 2px' }}>Goals pay when met.</span>
            </section>
          )}
          <section style={{ display: 'grid', gap: 8 }} data-testid="almanac-goals">
            <span style={{ ...SMALL, margin: '0 2px' }} data-testid="goals-tally">
              <Figs text={`${met} of ${total} met`} />
            </span>
            {goals
              .filter((g) => !g.long)
              .map((g) => (
                <GoalCard key={g.id} row={g} />
              ))}
            {goals.some((g) => g.long) && (
              // D334: long goals run across years and are kept until the world is passed on.
              <div style={{ display: 'grid', gap: 6 }} data-testid="almanac-long-goals">
                <span style={{ font: '600 var(--fs-label)/1.3 var(--font-ui)' }}>
                  {cap(term('generation'))} goals
                </span>
                <span style={SMALL}>
                  These carry over from year to year until you {lower(term('passOn'))}.
                </span>
                {goals
                  .filter((g) => g.long)
                  .map((g) => (
                    <GoalCard key={g.id} row={g} />
                  ))}
              </div>
            )}
          </section>
          <section style={{ display: 'grid', gap: 6 }} data-testid="almanac-collection">
            <SectionHead
              title="Collection"
              count={`${collection.reduce((a, g) => a + g.found, 0)} of ${collection.reduce(
                (a, g) => a + g.total,
                0,
              )} found`}
            />
            {collection.map((g) => (
              <div key={g.kind} style={{ display: 'grid', gap: 6 }}>
                <span style={{ ...SMALL, fontWeight: 600 }}>
                  {g.label} <Figs text={`${g.found} of ${g.total}`} />
                </span>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(64px, 1fr))',
                    gap: 6,
                  }}
                >
                  {g.rows.map((row) => (
                    <CollectionCell key={row.id} row={row} />
                  ))}
                </div>
              </div>
            ))}
          </section>
        </>
      )}
    </div>
  );
}
